/** An error answer from the API, with per-field messages when the input was invalid. */
export class ApiError extends Error {
  status: number
  fieldErrors: Record<string, string[]>

  constructor(status: number, message: string, fieldErrors: Record<string, string[]> = {}) {
    super(message)
    this.status = status
    this.fieldErrors = fieldErrors
  }
}

type Method = 'GET' | 'POST' | 'PUT'

/**
 * Calls the API through this site's /api path (Next.js forwards it to the Node.js API, so the
 * browser never talks to the API or the database directly). The browser adds the sign-in cookie
 * by itself (same site, HttpOnly), so nothing about the user is sent from page code.
 */
export async function api<T>(path: string, method: Method = 'GET', body?: unknown): Promise<T> {
  return send<T>(path, method, body === undefined ? undefined : JSON.stringify(body), body !== undefined)
}

/**
 * Uploads a file (plus any extra form fields) as "multipart/form-data",
 * the format browsers use for file uploads. Files cannot travel inside JSON.
 */
export async function upload<T>(path: string, fields: Record<string, string | Blob>): Promise<T> {
  const form = new FormData()
  for (const [name, value] of Object.entries(fields)) form.append(name, value)
  return send<T>(path, 'POST', form, false)
}

async function send<T>(path: string, method: Method, body: BodyInit | undefined, isJson: boolean): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  // For FormData the browser sets Content-Type itself (it includes a generated boundary).
  if (isJson) headers['Content-Type'] = 'application/json'

  let res: Response
  try {
    res = await fetch(`/api${path}`, { method, headers, body })
  } catch {
    throw new ApiError(0, 'Cannot reach the server. Is the backend running?')
  }

  const text = await res.text()
  let json = null
  try {
    json = text ? JSON.parse(text) : null
  } catch {
    // Not JSON (e.g. the proxy's own error page when the backend is down).
  }

  if (!res.ok) {
    // Signed out on the server (expired, or deactivated): tell the app to show the sign-in page.
    if (res.status === 401 && !path.startsWith('/auth/')) window.dispatchEvent(new Event(UNAUTHORIZED))
    if (res.status === 429) throw new ApiError(429, 'Too many attempts. Wait a minute and try again.')
    // The API sends "problem details": { title, status, errors?: { field: [messages] } }
    const errors: Record<string, string[]> = json?.errors ?? {}
    throw new ApiError(res.status, json?.title ?? `Request failed (${res.status})`, errors)
  }

  // Tell anything that shows live numbers (like the Overdue badge) that data has changed.
  if (method !== 'GET') window.dispatchEvent(new Event(DATA_CHANGED))

  return json as T
}

/** Browser event fired after every successful save. */
export const DATA_CHANGED = 'gcrm:data-changed'

/** Browser event fired when the server says the user is no longer signed in. */
export const UNAUTHORIZED = 'gcrm:unauthorized'
